import { useContext, useState } from "react"

import { 
  DataTable, 
  EllipsisDropdown, 
  EmptyPlaceholder, 
  GuardianStudentsList, 
  IntegrationTypeBadge, 
  MisBadge, 
  MisCountBadge, 
  Page, 
  PageHeader,
  SiteDataMenu,
} from "@components"

import { useLazyQuery, useQuery } from "@apollo/client"
import { ContextualSidebarContext, UserContext } from "@context"
import { Chip } from "@heroui/react"
import { CircleStackIcon } from "@heroicons/react/24/outline"

import { 
  GET_GUARDIANS, 
  GET_SITE_DATA_GUARDIANS_SUMMARY, 
  GET_SITE_DATA_GUARDIANS_XLS_DOWNLOAD_LINK 
} from "@graphql"

import { 
  downloadFile, 
  guardianFullname, 
  lastImportDuration, 
  showLoader,
  mySqlToLocal 
} from "@helpers"

import {   
  useColumnManager,
  useSetActiveSidebarLink 
} from "@hooks"

import { 
  columns, 
  defaultColumns, 
  downloadOptions, 
  filters 
} from "./data"

import AccountDetails from "./AccountDetails"
import { Stats } from "./Stats"

const Guardians = () => {
  const { user } = useContext(UserContext)
  const { setContextualSidebar } = useContext(ContextualSidebarContext)
  const [isDownloading, setIsDownloading] = useState(false)
  const [refetching, setRefetching] = useState(false)
  const [selectedFilters, setSelectedFilters] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [sortDescriptor, setSortDescriptor] = useState({ column: "surname", direction: "ascending" })
  const [page, setPage] = useState(1)
  const [perPage] = useState(50)

  useSetActiveSidebarLink("SITE_DATA_GUARDIANS", "SITE_DATA")
  const [selectedColumns, setSelectedColumns] = useColumnManager(columns, "data.guardians.columns", defaultColumns)

  const summaryData = useQuery(GET_SITE_DATA_GUARDIANS_SUMMARY, {
    variables: {
      clientId: user.me.client.id,
    },
    fetchPolicy: "cache-and-network",
    pollInterval: 900000,
  })

  const { data, loading, error, refetch, networkStatus } = useQuery(GET_GUARDIANS, {
    variables: {
      searchTerm,
      page,
      perPage,
      clientId: user.me.client.id,
      orderBy: [
        {
          column: sortDescriptor.column.toUpperCase(),
          order: sortDescriptor.direction === "ascending" ? "ASC" : "DESC",
        },
      ],
      filter: selectedFilters ? selectedFilters[0] : null,
    },
    
    fetchPolicy: "cache-and-network",
    pollInterval: 900000,
  })

  const [getSiteDataGuardiansXlsDownloadLink] = useLazyQuery(GET_SITE_DATA_GUARDIANS_XLS_DOWNLOAD_LINK, {
    variables: { clientId: user.me.client.id },
    fetchPolicy: "network-only",
    onCompleted: data => {
      downloadFile(data.dataGuardiansXlsDownload.filename, data.dataGuardiansXlsDownload.url)
      setIsDownloading(false)
    },
  })

  const handleRefetchButtonClicked = () => {
    setRefetching(true)
    refetch().then(() => setRefetching(false))
  }

  const handleDownload = type => {
    if (type === "xls") {
      setIsDownloading(true)
      getSiteDataGuardiansXlsDownloadLink()
    }
  }

  const handleSearchClear = () => {
    setSearchTerm("")
    setPage(1)
  }

  const handleSearchChange = searchTerm => {
    setPage(1)
    setSearchTerm(searchTerm)
  }

  const handleShowAccount = guardian =>
    setContextualSidebar(prevState => ({
      ...prevState,
      open: true,
      title: guardianFullname(guardian),
      subTitle: `Account Details`,
      content: <AccountDetails guardian={guardian} />,
      size: "lg",
    }))

  const handleShowStudents = guardian =>
    setContextualSidebar(prevState => ({
      ...prevState,
      open: true,
      title: guardianFullname(guardian),
      subTitle: `${guardianFullname(guardian)} students are listed below.`,
      content: <GuardianStudentsList guardian={guardian} />,
      size: "lg",
    }))

  const handleSortChange = sortDescriptor => {
    setSortDescriptor(sortDescriptor)
    setPage(1)
  }

  const handleToggleColumn = selectedColumns => {
    setSelectedColumns(Object.keys(columns).filter(column => selectedColumns.has(column)))
  }

  const handleToggleFilter = selectedFilter => {
    setSelectedFilters(selectedFilter.size > 0 ? [selectedFilter.currentKey] : [] )
  }

  const renderCell = (guardian, column) => {
    const DateChip = ({ value, color, isDate }) => 
      <Chip color={color} size="sm" className="text-center text-white min-w-36" classNames={{ content: "font-semibold" }}>
        {isDate ? mySqlToLocal(value).format("YYYY-MM-DD HH:mm") : value}
      </Chip>

    if (column === "email") {
      return guardian['user'][column]
    }

    if (column === "activated_at") {
      return guardian['user']['activated_at'] 
        ? <DateChip color="success" value={guardian['user'][column]} isDate={true} /> 
        : <DateChip color="warning" value="Not Active" isDate={false} />
    }

    if (column === "activation_email_last_sent") {
      return guardian['user'][column]
        ? <DateChip color="success" value={guardian['user'][column]} isDate={true} /> 
        : <DateChip color="warning" value="Not Sent" isDate={false} />
    }

    if (column === "last_login") {
      return guardian['user']['last_active']
        ? <DateChip color="success" value={guardian['user'][column]} isDate={true} /> 
        : <DateChip color="warning" value="Never" isDate={false} />
    }

    if (column === "phone") {
      return guardian['user']['mobile'] ? guardian['mobile'] : guardian['telephone'] ? guardian['telephone'] : "Unknown"
    }

    if (column === "actions") {
      const items = [
        { id: 1, handleClick: () => handleShowStudents(guardian), caption: "View Students" },
        { id: 2, handleClick: () => handleShowAccount(guardian), caption: "View / Edit Account" }
      ]

      return <EllipsisDropdown items={items} />
    }

    if (["created_at", "updated_at"].includes(column)) {
      return mySqlToLocal(guardian[column]).format("YYYY-MM-DD HH:mm")
    }

    if (column === "is_sync" && guardian.is_sync) {
      return (
        <Chip classNames={{ base: "bg-sb-sync", content: "text-white font-semibold" }} size="sm" variant="flat">
          Sync
        </Chip>
      )
    }

    if (column === "is_sync" && !guardian.is_sync) {
      return (
        <Chip classNames={{ base: "bg-sb-sync-custom", content: "text-white font-semibold" }} size="sm" variant="flat">
          Custom
        </Chip>
      )
    }

    return guardian[column]
  }

  if (error) return `Error! ${error.message}`

  const showLoadingPlaceholder = showLoader(loading, networkStatus, data?.client?.guardians?.data, refetching)

  return (
    <Page>
      <PageHeader
        title="Guardian Data"
        subTitle="Imported guardian data from your MIS / CSV file."
        icon={<CircleStackIcon className="page-header-icon" />}
        contentBottom={
          <div className="flex flex-wrap gap-2">
            <IntegrationTypeBadge name={user?.me?.client?.importType?.name || "unknown"} />
            <MisBadge name={user?.me?.client?.mis || "n/a"} />
            <MisCountBadge name={`${summaryData?.data?.dataGuardiansSummary?.total || 0} Guardians`} />
          </div>
        }
        actions={<SiteDataMenu clientId={user.me.client.id} />}
      />

      <Stats
        summary={{
          dataAge: summaryData?.data?.dataGuardiansSummary?.data_age,
          lastImport: lastImportDuration(summaryData?.data?.dataGuardiansSummary?.imported_at),
          total: summaryData?.data?.dataGuardiansSummary?.total,
          syncCount: summaryData?.data?.dataGuardiansSummary?.sync_count,
          customCount: summaryData?.data?.dataGuardiansSummary?.custom_count,
          missingStudentCount: summaryData?.data?.dataGuardiansSummary?.missing_student_count,
        }}
      />

      <DataTable
        columns={columns}
        currentPage={page}
        currentSortOrder={sortDescriptor}
        data={data?.client?.guardians?.data}
        disallowEmptyFilterSelection={false}
        downloadOptions={downloadOptions}
        filters={filters}
        filtersSelectionMode="single"
        handleDownload={type => handleDownload(type)}
        handlePageChange={page => setPage(page)}
        handleRefetch={() => handleRefetchButtonClicked()}
        handleRenderCell={(item, key) => renderCell(item, key)}
        handleSearchChange={searchTerm => handleSearchChange(searchTerm)}
        handleSearchClear={handleSearchClear}
        handleSortChange={sortDescriptor => handleSortChange(sortDescriptor)}
        handleToggleColumn={selectedColumns => handleToggleColumn(selectedColumns)}
        handleToggleFilter={selectedFilter => handleToggleFilter(selectedFilter)}
        isDownloading={isDownloading}
        loading={showLoadingPlaceholder}
        isRefetching={refetching}
        noDataMessage={<EmptyPlaceholder classNames="mt-2" title="No Guardians Found" description="No Guardians found! Try clearing any filters." />}
        paginatorInfo={data?.client?.guardians?.paginatorInfo}
        searchInputPlaceHolder="Search by forename / surname"
        searchTerm={searchTerm}
        selectedColumns={selectedColumns}
        selectedFilters={selectedFilters}
        showFilterCount={true}
        showDownloadButton={true}
        showRefetchButton={true}
      />
    </Page>
  )
}

export default Guardians
